import { useState, useEffect } from 'react';
import { api, Video } from '@/lib/api';
import { youtubeRssService } from '@/lib/youtubeRss';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

export const useHybridVideos = (type: 'all' | 'subscribed' | 'daily' = 'all', selectedDate?: Date) => {
  const [videos, setVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [usingRSSFallback, setUsingRSSFallback] = useState(false);
  const [usingCacheFallback, setUsingCacheFallback] = useState(false);
  const [newVideosCount, setNewVideosCount] = useState(0);
  const { toast } = useToast();
  const { user } = useAuth();

  // Convert cached video data to Video type
  const convertCachedToVideo = (cachedVideo: any): Video | null => {
    if (!cachedVideo) return null;
    
    try {
      return {
        id: cachedVideo.video_id,
        title: cachedVideo.title,
        thumbnail: cachedVideo.thumbnail,
        channelName: cachedVideo.channel_name,
        channelAvatar: cachedVideo.channel_avatar,
        channelId: cachedVideo.channel_id,
        views: cachedVideo.views || 0,
        published: cachedVideo.published,
        publishedDate: cachedVideo.published_date,
        duration: cachedVideo.duration
      };
    } catch (error) {
      console.error('Error converting cached video:', error);
      return null;
    }
  };

  // Convert RSS video data to Video type
  const convertRSSToVideo = (rssVideo: any): Video | null => {
    if (!rssVideo) return null;
    
    try {
      return {
        id: rssVideo.id,
        title: rssVideo.title,
        thumbnail: rssVideo.thumbnail,
        channelName: rssVideo.channelName,
        channelAvatar: '',
        channelId: rssVideo.channelId,
        views: 0,
        published: rssVideo.published,
        publishedDate: rssVideo.publishedDate,
        duration: '00:00'
      };
    } catch (error) {
      console.error('Error converting RSS video:', error);
      return null;
    }
  };

  // Save videos to cache
  const saveVideosToCache = async (videos: Video[], tableName: 'videos' | 'cached_videos_feed' = 'cached_videos_feed') => {
    if (!videos.length) return;
    
    try {
      const videosToSave = videos.map(video => ({
        video_id: video.id,
        title: video.title,
        thumbnail: video.thumbnail,
        channel_name: video.channelName,
        channel_avatar: video.channelAvatar || '',
        channel_id: video.channelId,
        views: video.views,
        published: video.published,
        published_date: video.publishedDate,
        duration: video.duration
      }));
      
      const { error } = await supabase
        .from(tableName)
        .upsert(videosToSave, { 
          onConflict: 'video_id',
          ignoreDuplicates: false 
        });
      
      if (error) {
        console.error(`Error saving videos to ${tableName}:`, error);
      } else {
        console.log(`Saved ${videos.length} videos to ${tableName} cache`);
      }
    } catch (error) {
      console.error(`Error in saveVideosToCache for ${tableName}:`, error);
    }
  };

  // Get cached videos
  const getCachedVideos = async (type: 'all' | 'subscribed' | 'daily', subscriptionIds?: string[], selectedDate?: Date): Promise<Video[]> => {
    try {
      console.log(`Fetching cached videos for ${type}...`);
      
      let query;
      if (type === 'all') {
        const [videosResult, cachedResult] = await Promise.all([
          supabase.from('videos').select('*').order('published_date', { ascending: false }).limit(100),
          supabase.from('cached_videos_feed').select('*').order('published_date', { ascending: false }).limit(100)
        ]);
        
        const allCachedVideos = [
          ...(videosResult.data?.map(convertCachedToVideo).filter(Boolean) || []),
          ...(cachedResult.data?.map(convertCachedToVideo).filter(Boolean) || [])
        ];
        
        const uniqueVideos = Array.from(
          new Map(allCachedVideos.map(video => [video.id, video])).values()
        );
        
        console.log(`Found ${uniqueVideos.length} unique cached videos from both tables`);
        return uniqueVideos as Video[];
      } else {
        query = supabase.from('cached_videos_feed').select('*');
      }
      
      if ((type === 'subscribed' || type === 'daily') && subscriptionIds && subscriptionIds.length > 0) {
        query = query.in('channel_id', subscriptionIds);
      }
      
      if (type === 'daily' || selectedDate) {
        const dateStr = selectedDate 
          ? selectedDate.toISOString().split('T')[0] 
          : new Date().toISOString().split('T')[0];
        query = query.eq('published_date', dateStr);
      }
      
      query = query.order('published_date', { ascending: false }).limit(200);
      
      const { data, error } = await query;
      
      if (error) {
        console.error('Error fetching cached videos:', error);
        return [];
      }
      
      console.log(`Found ${data?.length || 0} cached videos from cached_videos_feed table`);
      const convertedVideos = data?.map(convertCachedToVideo).filter(Boolean) || [];
      return convertedVideos as Video[];
    } catch (error) {
      console.error('Error in getCachedVideos:', error);
      return [];
    }
  };

  // Get user video source preference
  const getUserVideoSource = async (): Promise<string> => {
    if (!user) return 'rss';

    try {
      const { data } = await supabase
        .from('profiles')
        .select('video_source')
        .eq('id', user.id)
        .single();
      
      return data?.video_source || 'rss';
    } catch (error) {
      console.error('Error fetching user video source:', error);
      return 'rss';
    }
  };

  // Universal video fetching based on user preferences
  const getVideosBasedOnPreference = async (type: 'all' | 'subscribed' | 'daily', subscriptionIds?: string[]) => {
    try {
      console.log(`🚀 Starting video fetch for ${type} with user preferences`);
      
      // Reset fallback indicators
      setUsingRSSFallback(false);
      setUsingCacheFallback(false);

      const userVideoSource = await getUserVideoSource();
      console.log(`📺 User preferred source: ${userVideoSource}`);

      // 1. Primary source based on user preference
      try {
        let primaryVideos: Video[] = [];

        if (userVideoSource === 'rss') {
          console.log('📡 Using RSS as primary source...');
          if (type === 'all') {
            // RSS doesn't support trending, fallback to YouTube API
            console.log('⚠️ RSS doesn\'t support trending videos, using YouTube API...');
            primaryVideos = await api.getVideos();
          } else if (subscriptionIds && subscriptionIds.length > 0) {
            const rssVideos = await youtubeRssService.getMultipleChannelsRSSVideos(subscriptionIds);
            let filteredRssVideos = rssVideos;
            if (selectedDate) {
              const dateStr = selectedDate.toISOString().split('T')[0];
              filteredRssVideos = rssVideos.filter(video => video.publishedDate === dateStr);
            }
            primaryVideos = filteredRssVideos.map(convertRSSToVideo).filter(Boolean) as Video[];
            setUsingRSSFallback(true);
          }
        } else if (userVideoSource === 'youtube_api') {
          console.log('🎬 Using YouTube API as primary source...');
          if (type === 'all') {
            primaryVideos = await api.getVideos();
          } else if (subscriptionIds && subscriptionIds.length > 0) {
            primaryVideos = await api.getSubscribedVideos();
          }
        }

        if (primaryVideos.length > 0) {
          console.log(`✅ SUCCESS: ${primaryVideos.length} videos from primary source (${userVideoSource})`);
          const tableName = type === 'all' ? 'videos' : 'cached_videos_feed';
          await saveVideosToCache(primaryVideos, tableName);
          return primaryVideos;
        }

        console.log('❌ Primary source returned no videos, trying fallbacks...');
      } catch (primaryError) {
        console.error('❌ Primary source failed:', primaryError);
      }

      // 2. Fallback strategies
      if (userVideoSource !== 'rss' && (type === 'subscribed' || type === 'daily') && subscriptionIds && subscriptionIds.length > 0) {
        try {
          console.log('📡 Trying RSS as fallback...');
          const rssVideos = await youtubeRssService.getMultipleChannelsRSSVideos(subscriptionIds);
          let filteredRssVideos = rssVideos;
          if (selectedDate) {
            const dateStr = selectedDate.toISOString().split('T')[0];
            filteredRssVideos = rssVideos.filter(video => video.publishedDate === dateStr);
          }
          
          if (filteredRssVideos.length > 0) {
            const convertedRssVideos = filteredRssVideos.map(convertRSSToVideo).filter(Boolean) as Video[];
            setUsingRSSFallback(true);
            console.log(`✅ SUCCESS: ${convertedRssVideos.length} videos from RSS fallback`);
            await saveVideosToCache(convertedRssVideos);
            return convertedRssVideos;
          }
        } catch (rssError) {
          console.error('❌ RSS fallback failed:', rssError);
        }
      }

      if (userVideoSource !== 'youtube_api') {
        try {
          console.log('🎬 Trying YouTube API as fallback...');
          let apiVideos: Video[] = [];
          
          if (type === 'all') {
            apiVideos = await api.getVideos();
          } else if (subscriptionIds && subscriptionIds.length > 0) {
            apiVideos = await api.getSubscribedVideos();
          }
          
          if (apiVideos.length > 0) {
            console.log(`✅ SUCCESS: ${apiVideos.length} videos from YouTube API fallback`);
            const tableName = type === 'all' ? 'videos' : 'cached_videos_feed';
            await saveVideosToCache(apiVideos, tableName);
            return apiVideos;
          }
        } catch (apiError) {
          console.error('❌ YouTube API fallback failed:', apiError);
        }
      }

      // 3. Final fallback: Cache
      console.log('💾 Using cached data as final fallback...');
      setUsingCacheFallback(true);
      const cachedVideos = await getCachedVideos(type, subscriptionIds, selectedDate);
      
      if (cachedVideos.length > 0) {
        console.log(`✅ Using ${cachedVideos.length} videos from cache (final fallback)`);
        return cachedVideos;
      }
      
      console.log('❌ No videos found from any source');
      return [];
    } catch (error) {
      console.error('Fatal error in getVideosBasedOnPreference:', error);
      throw error;
    }
  };

  // Fetch videos function
  const fetchVideos = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Reset fallback states
      setUsingRSSFallback(false);
      setUsingCacheFallback(false);
      
      let subscriptionIds: string[] = [];
      
      // Get user subscriptions for subscribed/daily content
      if ((type === 'subscribed' || type === 'daily') && user) {
        const { data: userProfile } = await supabase
          .from('profiles')
          .select('subscriptions')
          .eq('id', user.id)
          .single();
        
        if (userProfile && userProfile.subscriptions) {
          subscriptionIds = userProfile.subscriptions;
        }
      }
      
      const fetchedVideos = await getVideosBasedOnPreference(type, subscriptionIds);
      setVideos(fetchedVideos);
      setNewVideosCount(fetchedVideos.length);
      
    } catch (err: any) {
      console.error('Error in fetchVideos:', err);
      setError(err.message || 'Failed to fetch videos');
      setVideos([]);
    } finally {
      setLoading(false);
    }
  };

  // Effect to fetch videos
  useEffect(() => {
    fetchVideos();
  }, [type, selectedDate, user]);

  return {
    videos,
    loading,
    error,
    usingRSSFallback,
    usingCacheFallback,
    newVideosCount,
    refetch: fetchVideos
  };
};